import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { getApiUrl } from '../../utils/config';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Mail, 
  Check, 
  X, 
  Calendar,
  User,
  Crown,
  Shield,
  Edit3,
  Eye,
  Clock,
  Building
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface UserInvitation {
  id: string;
  client_name: string;
  client_id: string;
  role: string;
  message?: string;
  inviter_name: string;
  expires_at: string;
  created_at: string;
  invitation_token: string;
}

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'owner': return <div className="w-4 h-4" />;
    case 'admin': return <Shield className="w-4 h-4" />;
    case 'editor': return <Edit3 className="w-4 h-4" />;
    case 'member': return <User className="w-4 h-4" />;
    case 'viewer': return <Eye className="w-4 h-4" />;
    default: return <User className="w-4 h-4" />;
  }
};

const getRoleColor = (role: string) => {
  switch (role) {
    case 'owner': return 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 dark:from-yellow-900/30 dark:to-amber-900/30 dark:text-yellow-300';
    case 'admin': return 'bg-gradient-to-r from-red-100 to-rose-100 text-red-800 dark:from-red-900/30 dark:to-rose-900/30 dark:text-red-300';
    case 'editor': return 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 dark:from-blue-900/30 dark:to-indigo-900/30 dark:text-blue-300';
    case 'member': return 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-300';
    case 'viewer': return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 dark:from-gray-900/30 dark:to-slate-900/30 dark:text-gray-300';
    default: return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 dark:from-gray-900/30 dark:to-slate-900/30 dark:text-gray-300';
  }
};

interface UserPendingInvitationsCardProps {
  onInvitationAccepted: () => void;
}

export const UserPendingInvitationsCard: React.FC<UserPendingInvitationsCardProps> = ({ onInvitationAccepted }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl()}/api/v1/invitations/pending`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvitations(data);
      } else {
        console.error('Failed to fetch invitations');
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async (invitationId: string) => {
    try {
      setProcessingInvitation(invitationId);
      
      const invitation = invitations.find(inv => inv.id === invitationId);
      if (!invitation) return;
      
      const response = await fetch(`${getApiUrl()}/api/v1/invitations/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ token: invitation.invitation_token }),
      });
      
      if (response.ok) {
        toast({
          title: "Success",
          description: `You've joined ${invitation.client_name} as ${invitation.role}`,
        });
        fetchInvitations();
        onInvitationAccepted();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to accept invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to accept invitation",
        variant: "destructive",
      });
    } finally {
      setProcessingInvitation(null);
    }
  };

  const declineInvitation = async (invitationId: string) => {
    try {
      setProcessingInvitation(invitationId);
      
      const invitation = invitations.find(inv => inv.id === invitationId);
      if (!invitation) return;
      
      const response = await fetch(`${getApiUrl()}/api/v1/invitations/decline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({ token: invitation.invitation_token }),
      });
      
      if (response.ok) {
        toast({
          title: "Invitation Declined",
          description: "You have declined the team invitation",
        });
        fetchInvitations();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to decline invitation');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to decline invitation",
        variant: "destructive",
      });
    } finally {
      setProcessingInvitation(null);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mb-4"></div>
        <div className="space-y-3">
          <div className="h-20 bg-slate-200 dark:bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  return (
    <motion.div 
      className="relative overflow-hidden rounded-2xl bg-white/90 dark:bg-slate-800/50 backdrop-blur-xl border border-slate-200/60 dark:border-slate-600/40 transition-all duration-300"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/30 dark:from-green-500/5 dark:via-emerald-500/5 dark:to-slate-500/5"></div>
      <div className="relative p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center space-x-2">
            <Mail className="w-5 h-5 text-green-500" />
            <span>Your Team Invitations</span>
          </h3>
          <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
            {invitations.length} pending
          </Badge>
        </div>
        
        <div className="space-y-4">
          <AnimatePresence>
            {invitations.map((invitation, index) => (
              <motion.div 
                key={invitation.id}
                className="group relative overflow-hidden rounded-xl bg-white/60 dark:bg-slate-700/40 backdrop-blur-sm border border-slate-200/40 dark:border-slate-600/30 hover:bg-white/80 dark:hover:bg-slate-700/60 transition-all duration-300 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-slate-900/20"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ scale: 1.01, y: -2 }}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/50 dark:to-emerald-900/50 rounded-lg flex items-center justify-center">
                        <Building className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-slate-800 dark:text-slate-100">
                            {invitation.client_name}
                          </h4>
                          <Badge className={`${getRoleColor(invitation.role)} border-0 shadow-sm`}>
                            <div className="flex items-center space-x-1">
                              {getRoleIcon(invitation.role)}
                              <span className="capitalize font-medium">{invitation.role}</span>
                            </div>
                          </Badge>
                        </div>
                        
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                          <span className="font-medium">{invitation.inviter_name}</span> invited you to join their team
                        </p>
                        
                        {invitation.message && (
                          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2 mb-2">
                            <p className="text-xs text-slate-700 dark:text-slate-300 italic">
                              "{invitation.message}"
                            </p>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-3 text-xs text-slate-500 dark:text-slate-400">
                          <div className="flex items-center space-x-1">
                            <Calendar className="w-3 h-3" />
                            <span>Invited {new Date(invitation.created_at).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>Expires {new Date(invitation.expires_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-3">
                      <Button
                        onClick={() => declineInvitation(invitation.id)}
                        disabled={processingInvitation === invitation.id}
                        variant="outline"
                        size="sm"
                        className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Decline
                      </Button>
                      <Button
                        onClick={() => acceptInvitation(invitation.id)}
                        disabled={processingInvitation === invitation.id}
                        size="sm"
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white border-0 shadow-lg shadow-green-500/25 transition-all duration-300 hover:shadow-xl hover:shadow-green-500/30"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Accept
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};